import {
  PositionSort,
  PositionsSortField,
  WalletPosition,
} from "@/api/positions/types";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { getHoursSince } from "@/utils/date";
import { applyMinMaxFilter } from "@/utils/filters";
import { createContext, useContext, useState, ReactNode } from "react";

export interface PositionFilters {
  minInvested?: number;
  maxInvested?: number;
  minNetPNL?: number;
  maxNetPNL?: number;
  minNetROI?: number;
  maxNetROI?: number;
  minBalance?: number;
  maxBalance?: number;
  minTime?: number;
  maxTime?: number;
}

interface PositionsFilterContextProps {
  filters: PositionFilters;
  searchInput: string;
  setSearchInput: (query: string) => void;
  setFilters: (filters: PositionFilters) => void;
  updateFilter: (key: keyof PositionFilters, value: number) => void;
  clearFilters: () => void;
  filterPositions: (positions: WalletPosition[]) => WalletPosition[];
  defaultFilters: PositionFilters;
  sort: PositionSort;
  handleSortChange: (sortField: PositionsSortField) => void;
}

const defaultFilters: PositionFilters = {
  minInvested: undefined,
  maxInvested: undefined,
  minNetPNL: undefined,
  maxNetPNL: undefined,
  minNetROI: undefined,
  maxNetROI: undefined,
  minBalance: undefined,
  maxBalance: undefined,
  minTime: undefined,
  maxTime: undefined,
};

const PositionsFilterContext =
  createContext<PositionsFilterContextProps | null>(null);

export const PositionsFilterProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [filters, setFilters] = useState<PositionFilters>(defaultFilters);

  const [searchInput, setSearchInput] = useState("");
  const searchQuery = useDebouncedValue(searchInput, 300);

  const [sort, setSort] = useState<PositionSort>({
    sortField: "time",
    sortType: "desc" as "asc" | "desc",
  });

  const updateFilter = (key: keyof PositionFilters, value: number) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters(defaultFilters);
  };

  const handleSortChange = (sortField: PositionsSortField) => {
    setSort((prevSort) => {
      if (prevSort.sortField === sortField) {
        return {
          ...prevSort,
          sortType: prevSort.sortType === "asc" ? "desc" : "asc",
        };
      }
      return { sortField, sortType: "desc" };
    });
  };

  const filterPositions = (positions: WalletPosition[]) => {
    const filtered = positions.filter((pos) => {
      if (
        searchQuery &&
        !pos.token_name?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !pos.token_symbol?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !pos.token_address?.toLowerCase().includes(searchQuery.toLowerCase())
      ) {
        return false;
      }

      return (
        applyMinMaxFilter(
          pos.invested,
          filters.minInvested,
          filters.maxInvested
        ) &&
        applyMinMaxFilter(pos.net_pnl, filters.minNetPNL, filters.maxNetPNL) &&
        applyMinMaxFilter(
          pos.net_pnl_percentage,
          filters.minNetROI,
          filters.maxNetROI
        ) &&
        applyMinMaxFilter(
          pos.balance,
          filters.minBalance,
          filters.maxBalance
        ) &&
        applyMinMaxFilter(
          getHoursSince(pos.duration),
          filters.minTime,
          filters.maxTime
        )
      );
    });

    return filtered.sort((a, b) => {
      let aValue: number | string = 0;
      let bValue: number | string = 0;

      switch (sort.sortField) {
        case "token":
          aValue = a.token_name?.toLowerCase() || "";
          bValue = b.token_name?.toLowerCase() || "";
          break;
        case "invested":
          aValue = a.invested ?? 0;
          bValue = b.invested ?? 0;
          break;
        case "netPnl":
          aValue = a.net_pnl ?? 0;
          bValue = b.net_pnl ?? 0;
          break;
        case "netRoi":
          aValue = a.net_pnl_percentage ?? 0;
          bValue = b.net_pnl_percentage ?? 0;
          break;
        case "balance":
          aValue = a.balance;
          bValue = b.balance;
          break;
        case "time":
          aValue = getHoursSince(b.duration) ?? 0;
          bValue = getHoursSince(a.duration) ?? 0;
          break;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sort.sortType === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sort.sortType === "asc"
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    });
  };

  return (
    <PositionsFilterContext.Provider
      value={{
        filters,
        searchInput,
        setSearchInput,
        setFilters,
        updateFilter,
        clearFilters,
        filterPositions,
        defaultFilters,
        sort,
        handleSortChange,
      }}
    >
      {children}
    </PositionsFilterContext.Provider>
  );
};

export const usePositionsFilter = () => {
  const context = useContext(PositionsFilterContext);
  if (!context)
    throw new Error("usePositionsFilter must be used within a Provider");
  return context;
};
